import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tag, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import DeviceEndOfSupportContext from '@totem/components/deviceEndOfSupport/deviceEndOfSupportContext';
import { EndOfSupportRow } from '@totem/components/deviceEndOfSupport/types';
import {
  dateBasis,
  daysUntil,
  FILTER_VALUE,
  FilterKey,
  formatDate,
  NOT_RECORDED,
  STATUS,
} from '@totem/components/deviceEndOfSupport/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

import './deviceEndOfSupport.css';

const Sub = ({
  children,
  warning,
}: {
  children: React.ReactNode;
  warning?: boolean;
}) => (
  <div styleName="sub">
    <Typography.Text type={warning ? 'warning' : 'secondary'}>
      {children}
    </Typography.Text>
  </div>
);

const NotRecorded = () => (
  <Typography.Text type="secondary">{NOT_RECORDED}</Typography.Text>
);

const yearsAgo = (date: string) =>
  `${Math.round((-daysUntil(date) / 365) * 10) / 10} yr ago`;

const EndOfSupportTable = () => {
  const { loading, rows, filteredRows, filters, setFilter, sort, setSort } =
    useContext(DeviceEndOfSupportContext);
  const [pageSize, setPageSize] = useState<number>(25);

  // Options and counts come from every row, so a filter never hides its own choices.
  const filterProps = (key: FilterKey) => {
    const counts = new Map<string, number>();
    rows.forEach((row) => {
      const value = FILTER_VALUE[key](row);
      counts.set(value, (counts.get(value) ?? 0) + 1);
    });
    const values = [...counts.keys()].sort((left, right) => {
      if (left === NOT_RECORDED) {
        return 1;
      }
      if (right === NOT_RECORDED) {
        return -1;
      }
      return left.localeCompare(right);
    });
    return {
      filterMultiple: true,
      filterSearch: values.length > 8,
      filters: values.map((value) => ({
        text: `${value} (${counts.get(value)})`,
        value,
      })),
      filteredValue: filters[key]?.length ? filters[key] : null,
    };
  };

  const sortProps = (key: string) => ({
    sorter: true,
    sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    sortOrder: sort.key === key ? sort.order : null,
  });

  const columns: ColumnProps<EndOfSupportRow>[] = [
    {
      title: 'Device',
      key: 'name',
      fixed: 'left',
      width: 180,
      ...sortProps('name'),
      render: (_, row) => (
        <Link to={`/dashboard/devices/${row.id}`}>
          <strong>{row.name}</strong>
        </Link>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      ...sortProps('status'),
      ...filterProps('status'),
      render: (_, row) => (
        <Tag color={STATUS[row.status].color}>{STATUS[row.status].label}</Tag>
      ),
    },
    {
      title: 'End of support',
      key: 'eos',
      ...sortProps('eos'),
      render: (_, row) => {
        if (!row.eos) {
          return <NotRecorded />;
        }
        return (
          <>
            {formatDate(row.eos)}
            {row.status === 'check' && (
              <Sub warning>Date may not apply to this OS</Sub>
            )}
            {row.status === 'past' && <Sub>{yearsAgo(row.eos)}</Sub>}
            {row.status === 'soon' && <Sub>in {daysUntil(row.eos)} days</Sub>}
            <Sub>{dateBasis(row)}</Sub>
          </>
        );
      },
    },
    {
      title: 'End of life',
      key: 'eol',
      ...sortProps('eol'),
      render: (_, row) =>
        row.eol ? (
          <>
            {formatDate(row.eol)}
            {row.eolFirst && <Sub warning>Before end of support</Sub>}
          </>
        ) : (
          <NotRecorded />
        ),
    },
    {
      title: 'Managed',
      key: 'managed',
      ...filterProps('managed'),
      render: (_, row) =>
        row.managed ? (
          'Managed'
        ) : (
          <Typography.Text type="secondary">Not managed</Typography.Text>
        ),
    },
    {
      title: 'Type',
      key: 'type',
      ...filterProps('type'),
      render: (_, row) => row.type || <NotRecorded />,
    },
    {
      title: 'Vendor',
      key: 'vendor',
      ...filterProps('vendor'),
      render: (_, row) => row.vendor || <NotRecorded />,
    },
    {
      title: 'Operating system',
      key: 'os',
      ...filterProps('os'),
      render: (_, row) => (
        <>
          {row.os || <NotRecorded />}
          {row.firmware && <Sub>{row.firmware}</Sub>}
        </>
      ),
    },
    {
      title: 'Date applies to',
      key: 'productType',
      ...filterProps('productType'),
      render: (_, row) => (
        <>
          {row.productType || <NotRecorded />}
          {row.discontinued && (
            <Sub>Discontinued {formatDate(row.discontinued)}</Sub>
          )}
        </>
      ),
    },
    {
      title: 'Building',
      key: 'building',
      ...sortProps('building'),
      ...filterProps('building'),
      render: (_, row) => (
        <>
          {row.building ? (
            row.buildingId ? (
              <Link to={`/dashboard/buildings/${row.buildingId}`}>
                {row.building}
              </Link>
            ) : (
              row.building
            )
          ) : (
            <NotRecorded />
          )}
          {row.region && <Sub>{row.region}</Sub>}
        </>
      ),
    },
    {
      title: 'Control system',
      key: 'controlSystem',
      ...filterProps('controlSystem'),
      render: (_, row) =>
        row.controlSystem ? (
          row.controlSystemId ? (
            <Link to={`/dashboard/controlsystems/${row.controlSystemId}`}>
              {row.controlSystem}
            </Link>
          ) : (
            row.controlSystem
          )
        ) : (
          <NotRecorded />
        ),
    },
  ];

  return (
    <IBTable
      columns={columns}
      dataSource={filteredRows}
      loading={loading}
      rowKey="id"
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'No devices match these filters.' }}
      pagination={{
        defaultCurrent: 1,
        pageSize,
        showSizeChanger: true,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
      onChange={(_, tableFilters, sorter, extra) => {
        if (extra.action === 'filter') {
          (Object.keys(FILTER_VALUE) as FilterKey[]).forEach((key) =>
            setFilter(key, (tableFilters[key] as string[]) ?? []),
          );
        }
        if (extra.action === 'sort') {
          const { columnKey, order } = sorter as SorterResult<EndOfSupportRow>;
          setSort({
            key: order ? String(columnKey) : null,
            order: order ?? null,
          });
        }
      }}
    />
  );
};

export default EndOfSupportTable;
